import React from 'react';
import { toast } from 'react-toastify';
import { buildGlobalSettingsPatch, IGlobalStorefrontSettings } from '../../../../models/Storefront';
import { useGetGlobalStorefrontSettingsQuery, usePatchGlobalStorefrontSettingsMutation } from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import useCan, { SMTP_ADMIN } from '../../../../permissions/ability';
import { Stack, Pivot, PivotItem, MessageBar } from '@fluentui/react';
import SubExtensionDropdown from '../../../../components/ExtensionFolios/SubExtensionDropdown';
import GlobalStorefrontOpportunityExtensionForm from '../../../../components/Storefront/GlobalStorefrontOpportunityExtensionForm';
import StorefrontOpportunityExtensionSelection from '../../../../components/Storefront/StorefrontOpportunityExtensionSelection';
import { ExtensionType } from '../../../../Constants';
import ToggleField from '../../../../components/Common/ToggleField';
import EmailIntegration from '../../../../components/Storefront/Smtp/EmailIntegration';
import { useUrlTabState } from '../../../../hooks/useUrlState';
import ConditionalSetting from '../../../../components/Common/ConditionalSetting';

const DEBOUNCE_MS = 1500;

const StorefrontGlobalIntegrationsPage: React.FC = () => {
    // In the URL so a documentation link can land on the tab holding the setting it describes.
    const [selectedTab, setSelectedTab] = useUrlTabState('opportunities');
    const globalSettingsQuery = useGetGlobalStorefrontSettingsQuery();
    const [patchGlobalSettings] = usePatchGlobalStorefrontSettingsMutation();

    // SMTP is a paid, entitlement-gated add-on: the smtpAdmin permission is only granted when the
    // org has the SmtpEnabled entitlement (or Enterprise product level). Hiding is UX only; the
    // API enforces the real gate.
    const showEmailTab = useCan(SMTP_ADMIN);

    const [localSettings, setLocalSettingsRaw] = React.useState<IGlobalStorefrontSettings>();
    const localSettingsRef = React.useRef<IGlobalStorefrontSettings>();
    const originalSettingsRef = React.useRef<IGlobalStorefrontSettings>();
    const debounceSaveRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const setLocalSettings = React.useCallback((s: IGlobalStorefrontSettings) => {
        localSettingsRef.current = s;
        setLocalSettingsRaw(s);
    }, []);

    React.useEffect(() => {
        if (globalSettingsQuery.data && !originalSettingsRef.current) {
            setLocalSettings(globalSettingsQuery.data);
            originalSettingsRef.current = globalSettingsQuery.data;
        }
    }, [globalSettingsQuery.data, setLocalSettings]);

    React.useEffect(() => {
        return () => {
            if (debounceSaveRef.current) {
                clearTimeout(debounceSaveRef.current);
                if (localSettingsRef.current && originalSettingsRef.current) {
                    const patch = buildGlobalSettingsPatch(originalSettingsRef.current, localSettingsRef.current);
                    if (Object.keys(patch).length > 0) {
                        patchGlobalSettings(patch);
                    }
                }
            }
        };
    }, []);

    const doSave = React.useCallback(async (current: IGlobalStorefrontSettings) => {
        if (!originalSettingsRef.current) return;
        const patch = buildGlobalSettingsPatch(originalSettingsRef.current, current);
        if (Object.keys(patch).length === 0) return;
        try {
            await patchGlobalSettings(patch).unwrap();
            originalSettingsRef.current = { ...originalSettingsRef.current, ...patch };
            toast.success('Settings saved');
        } catch {
            toast.error('Failed to save settings');
        }
    }, [patchGlobalSettings]);

    const scheduleSave = React.useCallback(() => {
        if (debounceSaveRef.current) clearTimeout(debounceSaveRef.current);
        debounceSaveRef.current = setTimeout(() => {
            if (localSettingsRef.current) doSave(localSettingsRef.current);
        }, DEBOUNCE_MS);
    }, [doSave]);

    const handleChange = React.useCallback((field: keyof IGlobalStorefrontSettings, value: unknown) => {
        const base = localSettingsRef.current ?? localSettings;
        if (!base) return;
        const updated: IGlobalStorefrontSettings = { ...base, currency: base.currency ?? '', [field]: value };
        setLocalSettings(updated);
        scheduleSave();
    }, [setLocalSettings, scheduleSave]);

    if (globalSettingsQuery.isLoading || !localSettings) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <h1>Integrations</h1>
            <Pivot selectedKey={selectedTab} onLinkClick={(item) => setSelectedTab(item?.props.itemKey)}>
                <PivotItem headerText='Opportunities' itemKey='opportunities'>
                    <MessageBar>The opportunity extension will create a new opportunity in your CRM upon checkout in your Storefront. The following settings are used as default settings, and can be overridden at the Storefront level.</MessageBar>
                    <ToggleField
                        settingId='storefront.global.integrations.enableOpportunityCreation'
                        inline
                        label='Enable Opportunity Extension'
                        checked={localSettings.enableOpportunityCreation ?? false}
                        onChange={(e, v) => handleChange('enableOpportunityCreation', v ?? false)}
                    />
                    <StorefrontOpportunityExtensionSelection
                        extension={localSettings.opportunityExtension}
                        onChange={(v) => handleChange('opportunityExtension', v)}
                    />
                    {localSettings.enableOpportunityCreation &&
                        <GlobalStorefrontOpportunityExtensionForm
                            subExtensionFolioCompositeId={localSettings.opportunityExtension}
                        />
                    }
                    <ConditionalSetting
                        when={!!(localSettings.enableOpportunityCreation && localSettings.opportunityExtension)}
                        id='storefront.global.integrations.enableSalesOrderCreation'
                    >
                        <ToggleField
                            settingId='storefront.global.integrations.enableSalesOrderCreation'
                            inline
                            label='Create Sales Order on Order Placement'
                            checked={localSettings.enableSalesOrderCreation ?? false}
                            onChange={(e, v) => handleChange('enableSalesOrderCreation', v ?? false)}
                        />
                        <MessageBar>When enabled, the opportunity created on checkout is automatically converted into a sales order in the same CRM. Can be overridden at the Storefront level.</MessageBar>
                    </ConditionalSetting>
                </PivotItem>
                <PivotItem headerText='Customers' itemKey='customers'>
                    <Stack tokens={{childrenGap: '1em'}}>
                        <MessageBar>If set, this extension will be used during customer signup to search your CRM for a related record.</MessageBar>
                        <SubExtensionDropdown
                            settingId='storefront.global.integrations.customerLookupExtension'
                            isGlobal={true}
                            type={ExtensionType.StorefrontCustomer}
                            label='Customer Lookup Extension'
                            extension={localSettings.customerLookupExtension}
                            onChange={(v) => handleChange('customerLookupExtension', v)}
                        />
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Locations' itemKey='locations'>
                    <Stack tokens={{childrenGap: '1em'}}>
                        <MessageBar>The location lookup feature allows customers to choose their shipping and billing addresses from a dropdown populated with locations from your CRM. In order to use this feature, the customer must be linked to an active customer in your CRM.</MessageBar>
                        <ToggleField
                            settingId='storefront.global.integrations.locationLookupEnabled'
                            inline
                            label='Enable Location Lookup'
                            checked={localSettings.locationLookupEnabled ?? false}
                            onChange={(e, v) => handleChange('locationLookupEnabled', v ?? false)}
                        />
                        <SubExtensionDropdown
                            settingId='storefront.global.integrations.locationLookupExtension'
                            isGlobal={true}
                            type={ExtensionType.Locations}
                            label='Location Lookup Extension'
                            extension={localSettings.locationLookupExtension}
                            onChange={(v) => handleChange('locationLookupExtension', v)}
                        />
                    </Stack>
                </PivotItem>
                {showEmailTab &&
                    <PivotItem headerText='Email' itemKey='email'>
                        <EmailIntegration />
                    </PivotItem>
                }
            </Pivot>
        </div>
    );
};

export default StorefrontGlobalIntegrationsPage;
